import { cva, type VariantProps } from 'class-variance-authority';
import type { ReactNode } from 'react';

import { cn } from '../lib/cn';

/**
 * The funnel's `.badge` (11px, uppercase, 0.5px tracking) promoted to a status pill at
 * `--radius-full` for admin tables (tokens doc §7.7 at pill scale, §10.3).
 */
const badge = cva(
  'inline-flex items-center gap-1 rounded-full px-[10px] py-[2px] text-2xs font-semibold tracking-badge uppercase',
  {
    variants: {
      tone: {
        /*
         * M11.11. A pill is 11px on a tinted surface, which is the hardest contrast in
         * the system: `--color-neutral-500` on `--color-neutral-100` is 4.34:1 and
         * `--color-info-ink` on `--color-info-subtle` is 4.10:1, both short of AA. The
         * inks step down to the next neutral and to `--color-primary-accessible` — the
         * accessible sibling the token file already defines for exactly this, "the brand
         * blue darkened until it clears 4.5:1 both ways". No token value changes.
         */
        neutral: 'bg-neutral-100 text-neutral-600',
        primary: 'bg-primary-subtle text-primary-accessible',
        info: 'bg-info-subtle text-primary-accessible',
        success: 'bg-success-subtle text-success-ink',
        warning: 'bg-warning-subtle text-warning-ink',
        danger: 'bg-danger-subtle text-danger-ink',
      },
    },
    defaultVariants: { tone: 'neutral' },
  },
);

export type BadgeTone = NonNullable<VariantProps<typeof badge>['tone']>;

export interface BadgeProps extends VariantProps<typeof badge> {
  /**
   * A small glyph before the label. State must never be carried by color alone
   * (rules §48.5) — either pass an icon, or keep the label itself meaningful
   * ("Paid", "Failed"), which is the default.
   */
  icon?: ReactNode;
  className?: string;
  children: ReactNode;
}

export function Badge({ tone, icon, className, children }: BadgeProps) {
  return (
    <span className={cn(badge({ tone }), className)}>
      {icon ?? null}
      {children}
    </span>
  );
}
